import React from 'react';
import {connect} from 'react-redux';

import {assert} from 'Asserts';
import * as RegistrationTableUtil from 'notebook/registration-tables/RegistrationTableUtil';
import AddStageRunSamplesModal from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/AddStageRunSamplesModal';
import {TransferOperation, TransferOperationList} from 'notebook/transfer-tables/TransferTableConstants';
import * as TransferTableUtil from 'notebook/transfer-tables/TransferTableUtil';
import {Note} from 'notebook/util/NoteUtilConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import useTagSchemaIdsQuery from 'schemas/useTagSchemaIdsQuery';
import ModalAnchor from 'ui-library/ModalAnchor';
import {ElementMouseEvent} from 'util/Types';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';
import * as StageRunActions from 'workflows/StageRunActions';
import * as StageRunUtil from 'workflows/util/StageRunUtil';
import {
  RegistrationRecordList,
  StageRun,
  StageRunSample,
  StageRunSampleDetails,
  StageRunSampleKey,
} from 'workflows/WorkflowConstants';

import getOutputTableRowId from './getOutputTableRowId';
import OutputRichObjectTable from './OutputRichObjectTable';
import {UnversionedTable} from './UnversionedTable';

interface ConnectedProps {
  registrationRecordListsById: {[id: string]: RegistrationRecordList};
  transferOperationListsById: {[id: string]: TransferOperationList};
  transferOperationsById: {[id: string]: TransferOperation};
  orderedOutputSamples: Array<StageRunSampleKey>;
}

function mapStateToProps(state: BenchlingState, props: BaseOutputTableProps): ConnectedProps {
  const workflowSelector = new WorkflowSelector(state.models.workflows);
  const orderedOutputSamples = workflowSelector.getOrderedOutputSampleKeys(props.stageEntryId);
  assert(orderedOutputSamples != null, 'orderedOutputSamples are expected to be loaded');
  return {
    registrationRecordListsById: state.models.registrationRecordListsById,
    transferOperationListsById: state.models.transferTables.transferOperationListsById,
    transferOperationsById: state.models.transferTables.transferOperationsById,
    orderedOutputSamples: orderedOutputSamples || [],
  };
}

interface BaseOutputTableProps {
  note: Note;
  stageEntryId: string;
  stageRuns: Array<StageRun>;
  stageRunSampleById: {[id: string]: StageRunSample};
  locked: boolean;
  viewer: boolean;
}

export interface OutputTableProps extends BaseOutputTableProps, ConnectedProps {
  tagSchemaIds: Set<string>;
}

interface State {
  selectedRowIds: {[rowId: string]: boolean};
  headerCheckBoxSelected: boolean;
  isAddSamplesModalOpen: boolean;
}

export class OutputTable extends React.Component<OutputTableProps, State> {
  override state: State = {
    selectedRowIds: {},
    headerCheckBoxSelected: false,
    isAddSamplesModalOpen: false,
  };

  /**
   * sampleDetails is an array of {stageRun, stageRunSample}.
   */
  _addOutputSamples = (sampleDetails: Array<StageRunSampleDetails>): void => {
    StageRunActions.addOutputSamples(
      sampleDetails.map(({stageRun, stageRunSample}) => ({
        stageRunId: stageRun.id,
        stageRunSampleId: stageRunSample.id,
      }))
    );
  };

  _removeOutputSamples = (e: ElementMouseEvent): void => {
    // Prevent table from collapsing/expanding
    e.stopPropagation();
    const samplesToRemove = [];
    for (const stageRun of this.props.stageRuns) {
      for (const stageRunSampleId of stageRun.outputSampleIds) {
        const rowId = getOutputTableRowId(stageRun.id, stageRunSampleId);
        if (this.state.selectedRowIds[rowId]) {
          samplesToRemove.push({stageRunId: stageRun.id, stageRunSampleId});
        }
      }
    }
    StageRunActions.bulkRemoveOutputSamples(samplesToRemove, {
      onSuccess: () => {
        this.setState({selectedRowIds: {}});
      },
    });
  };

  _toggleSelection = (rowId: string): void => {
    if (this.state.selectedRowIds[rowId]) {
      this.setState({selectedRowIds: {...this.state.selectedRowIds, [rowId]: false}});
    } else {
      this.setState({selectedRowIds: {...this.state.selectedRowIds, [rowId]: true}});
    }
  };

  _toggleHeaderCheckbox = (): void => {
    if (this.state.headerCheckBoxSelected) {
      this.setState({
        headerCheckBoxSelected: false,
        selectedRowIds: {},
      });
    } else {
      const selectedRowIds: {[rowId: string]: boolean} = {};
      for (const stageRun of this.props.stageRuns) {
        for (const outputSampleId of stageRun.outputSampleIds) {
          selectedRowIds[getOutputTableRowId(stageRun.id, outputSampleId)] = true;
        }
      }
      this.setState({
        headerCheckBoxSelected: true,
        selectedRowIds,
      });
    }
  };

  _handleOpenAddSampleModal = (e: ElementMouseEvent): void => {
    // Prevent table from collapsing/expanding
    e.stopPropagation();
    this.setState({isAddSamplesModalOpen: true});
  };

  _handleCloseAddSampleModal = (): void => {
    this.setState({isAddSamplesModalOpen: false});
  };

  /**
   * Returns an object of arrays of {stageRun, stageRunSample} objects to present,
   * keyed on the source table name.
   */
  _getAvailableStageRunSampleDetailsByTableName(): StageRunUtil.SampleDetailsByTableName {
    const availableSampleKeys = StageRunUtil.getAvailableSampleKeysByTableName({
      stageRuns: this.props.stageRuns,
      registrationTables: RegistrationTableUtil.getRegistrationTablesInNote(this.props.note),
      registrationRecordListsById: this.props.registrationRecordListsById,
      tagSchemaIds: this.props.tagSchemaIds,
      transferTables: TransferTableUtil.getTransferTablesInNote(this.props.note),
      transferOperationListsById: this.props.transferOperationListsById,
      transferOperationsById: this.props.transferOperationsById,
    });
    const outputSampleKeys = StageRunUtil.getOutputSampleKeys(this.props.stageRuns);
    for (const tableName of Object.keys(availableSampleKeys)) {
      availableSampleKeys[tableName] = StageRunUtil.subtractSampleKeyLists(
        availableSampleKeys[tableName],
        outputSampleKeys
      );
    }
    return StageRunUtil.resolveSampleKeysByTableName(
      availableSampleKeys,
      this.props.stageRuns,
      this.props.stageRunSampleById
    );
  }

  _renderSamplesModal(): JSX.Element | false {
    if (!this.state.isAddSamplesModalOpen) {
      return false;
    }
    const stageRunSampleDetailsByTableName = this._getAvailableStageRunSampleDetailsByTableName();
    const tableIcons = StageRunUtil.getTableIconsByTableName({
      registrationTables: RegistrationTableUtil.getRegistrationTablesInNote(this.props.note),
      transferTables: TransferTableUtil.getTransferTablesInNote(this.props.note),
    });
    return (
      <ModalAnchor open={true}>
        <AddStageRunSamplesModal
          enableRowCounts={false}
          title="Add output samples"
          dropdownIconByTableName={tableIcons}
          stageRunSampleDetailsByTableName={stageRunSampleDetailsByTableName}
          onStageRunSamplesSelected={this._addOutputSamples}
          onRequestClose={this._handleCloseAddSampleModal}
        />
      </ModalAnchor>
    );
  }

  override render(): JSX.Element {
    if (!StageEntryStore.getEditorState(this.props.stageEntryId).isLatest) {
      return <UnversionedTable title="Output Samples" />;
    }

    return (
      <div>
        {this._renderSamplesModal()}
        <OutputRichObjectTable
          stageEntryId={this.props.stageEntryId}
          locked={this.props.locked}
          viewer={this.props.viewer}
          headerCheckBoxSelected={this.state.headerCheckBoxSelected}
          selectedRowIds={this.state.selectedRowIds}
          // Contents of table
          orderedOutputSamples={this.props.orderedOutputSamples}
          stageRuns={this.props.stageRuns}
          stageRunSampleById={this.props.stageRunSampleById}
          // Callbacks
          onOpenAddSampleModal={this._handleOpenAddSampleModal}
          onRemoveOutputSamples={this._removeOutputSamples}
          onToggleHeaderCheckbox={this._toggleHeaderCheckbox}
          onToggleSelection={this._toggleSelection}
        />
      </div>
    );
  }
}

function QueryingOutputTable(props: BaseOutputTableProps & ConnectedProps): JSX.Element {
  const tagSchemaIds = useTagSchemaIdsQuery();
  return <OutputTable {...props} tagSchemaIds={tagSchemaIds ?? new Set()} />;
}

export default connect(mapStateToProps)(QueryingOutputTable);
